import { Injectable } from '@angular/core';
import { API_HOST } from '../../../../core/api/api.config';
import { ApiService } from '../../../../core/api/api.service';
import { ENDPOINTS } from '../../../../core/api/endpoints';
import { CommonCodeGroup } from '../models/common-code.model';
import {
  CurrencyPayload,
  CurrencyRatePayload,
  GuarantorClientPayload,
  GuarantorPayload,
} from '../models/guarantor.model';
import { Observable, map, retry } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class Setup {
  data: { clientSeq: number; name: string }[] = [];

  constructor(private api: ApiService) {}

  getCurrentKibor(type: number): Observable<any> {
    return this.api.get(API_HOST.setup, `${ENDPOINTS.SETUP.get_kibor_current}?typ=${type}`);
  }

  getKiborHistory(type: number): Observable<any> {
    return this.api.get(API_HOST.setup, `${ENDPOINTS.SETUP.kibor_LIST}?typ=${type}`);
  }

  saveKibor(payload: any): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.kibor_SAVE, payload);
  }

  getclientList(): Observable<any[]> {
    return this.api.get(API_HOST.client, ENDPOINTS.CLIENT.GET_CLIENTS);
  }

  saveClient(payload: any): Observable<any> {
    return this.api.post(API_HOST.client, ENDPOINTS.CLIENT.SAVE_CLIENT, payload);
  }

  getPickList(groupCodes: number[]): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.pickList, groupCodes);
  }

  getCommonCodeGroups(): Observable<CommonCodeGroup[]> {
    return this.api.get<CommonCodeGroup[]>(API_HOST.setup, ENDPOINTS.SETUP.ref_grps);
  }

  saveCommonCodeGroup(payload: CommonCodeGroup): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.ref_grp_SAVE, payload);
  }

  getGuarantorList(): Observable<GuarantorPayload[]> {
    return this.api.get<GuarantorPayload[]>(API_HOST.setup, ENDPOINTS.SETUP.grntr_LIST);
  }

  saveGuarantor(payload: GuarantorPayload): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.grntr_SAVE, payload);
  }

  getGuarantorClientList(stpGrntrSeq: number): Observable<GuarantorClientPayload[]> {
    return this.api.get<GuarantorClientPayload[]>(API_HOST.setup, `${ENDPOINTS.SETUP.grntr_clnt_LIST}/${stpGrntrSeq}`);
  }

  saveGuarantorClient(payload: GuarantorClientPayload): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.grntr_clnt_SAVE, payload);
  }

  getCurrencyList(): Observable<any[]> {
    return this.api.get<any[]>(API_HOST.setup, ENDPOINTS.SETUP.crncy_LIST);
  }

  /** Active currencies only — for dropdowns. */
  getActiveCurrencyList(): Observable<any[]> {
    return this.api.get<any[]>(API_HOST.setup, ENDPOINTS.SETUP.crncy_ACT_LIST);
  }

  saveCurrency(payload: CurrencyPayload): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.crncy_SAVE, payload);
  }

  getCurrencyRateList(): Observable<any[]> {
    return this.api.get<any[]>(API_HOST.setup, ENDPOINTS.SETUP.crncy_rt_LIST);
  }

  saveCurrencyRate(payload: CurrencyRatePayload): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.crncy_rt_SAVE, payload);
  }

  getCommonCodeValues(grpSeq: number): Observable<any[]> {
    return this.api.get<any[]>(API_HOST.setup, `${ENDPOINTS.SETUP.ref_vals}/${grpSeq}`);
  }

  saveCommonCodeValue(payload: any): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.ref_val_SAVE, payload);
  }

  // FIX: correct generic
  getTypes(typCtgryKey: number): Observable<any[]> {
    return this.api.get<any[]>(API_HOST.setup, `${ENDPOINTS.SETUP.get_type}/${typCtgryKey}`);
  }

  savetype(payload: any): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_type, payload);
  }

  loadClientsLegacy(): void {
    this.getclientList().subscribe({
      next: (res: any[]) => {
        this.data = res.map((item) => ({
          clientSeq: item.clientSeq,
          name: item.clntNm,
        }));
      },
      error: (err) => console.error(err),
    });
  }

  saveCity(payload: {
    citySeq: number | null;
    cityDscr: string;
    cityNm: string;
    cityStsKey: number;
    delFlg: number;
  }): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_city, payload);
  }

  getQuestionnaireList(): Observable<any> {
    return this.api.get(API_HOST.setup, ENDPOINTS.SETUP.get_questionnaire_list);
  }

  getQuestionList(qstnrSeq: number): Observable<any> {
    return this.api.get(API_HOST.setup, `${ENDPOINTS.SETUP.get_question_list}/${qstnrSeq}`);
  }

  getActQuestionList(qstnrSeq: number): Observable<any> {
    return this.api.get(API_HOST.setup, `${ENDPOINTS.SETUP.get_act_question_list}/${qstnrSeq}`);
  }

  saveQuestionnaire(payload: any): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_questionnaire, payload);
  }

  saveQuestion(payload: any): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_question, payload);
  }

  saveAnswer(payload: any): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_answer, payload);
  }
  // ================= CITY =================
  getCity(): Observable<any[]> {
    return this.api.get(API_HOST.setup, ENDPOINTS.SETUP.get_cities);
  }

  // ================= EMPLOYEE REGISTRATION =================
  getAllEmployees(): Observable<any[]> {
    return this.api.get(API_HOST.setup, ENDPOINTS.SETUP.get_all_emps);
  }

  saveEmployee(payload: {
    empSeq: number | null;
    empCnic: number;
    empNm: string;
    empLanId: string;
    empId: number | null;
    hrid: string;
    delFlg: number;
  }): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_emp, payload);
  }

  // ================= ROLES (Admin service, called from Setup) =================
  getRoles(): Observable<any> {
    return this.api.get(API_HOST.admin, ENDPOINTS.ADMIN.GET_ROLES);
  }

  // ================= DEPARTMENT HEAD =================
  getAllDepartments(): Observable<any[]> {
    return this.api.get(API_HOST.setup, ENDPOINTS.SETUP.get_all_depts);
  }

  saveDepartment(payload: {
    deptSeq: number | null;
    deptNm: string;
    dscr: string;
    sts: boolean;
    delFlg: number;
  }): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_dept, payload);
  }

  saveDeptEmp(payload: {
    deptEmpSeq: number | null;
    deptSeq: number | null;
    empSeq: number;
    roleSeq: number | null;
    deptEmpNm: string;
    delFlg: number;
    cvrngEmpId: number;
    cvrngEmpFrmDt: string;
    cvrngEmpToDt: string;
  }): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_dept_emp, payload);
  }

  getDeptEmployees(deptSeq: number): Observable<any> {
    return this.api.get(API_HOST.setup, `${ENDPOINTS.SETUP.get_dept_emps}/${deptSeq}`);
  }

  saveTeamLead(payload: {
    orgTlSeq: number | null;
    deptSeq: number;
    empSeq: number;
    tmldNm: string;
    dscr: string;
    sts: boolean;
    delFlg: number;
    cvrngEmpId: number | null;
    cvrngEmpFrmDt: string | null;
    cvrngEmpToDt: string | null;
  }): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_team_lead, payload);
  }

  getTeamLeads(deptSeq: number): Observable<any> {
    return this.api.get(API_HOST.setup, `${ENDPOINTS.SETUP.get_team_lead}/${deptSeq}`);
  }

  saveRelationalOfficer(payload: {
    orgRoSeq: number | null;
    orgTlSeq: number;
    empSeq: number;
    roNm: string;
    dscr: string;
    sts: boolean;
    delFlg: number;
    cvrngEmpId: number | null;
    cvrngEmpFrmDt: string | null;
    cvrngEmpToDt: string | null;
  }): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_ro, payload);
  }

  getRelationalOfficers(orgTlSeq: number): Observable<any> {
    return this.api.get(API_HOST.setup, `${ENDPOINTS.SETUP.get_ros}/${orgTlSeq}`);
  }

  getAnswerList(questSeq: number): Observable<any[]> {
    return this.api.get(API_HOST.setup, `${ENDPOINTS.SETUP.get_answer_list}/${questSeq}`);
  }
  // ================= COUNTRY =================
  getCountries(): Observable<any[]> {
    return this.api.get(API_HOST.setup, ENDPOINTS.SETUP.get_country_list);
  }

  // ================= STATE =================
  getStates(countrySeq: number): Observable<any[]> {
    return this.api.get(API_HOST.setup, `${ENDPOINTS.SETUP.get_state_list}/${countrySeq}`);
  }

  // ================= DISTRICT =================
  getDistricts(stateSeq: number): Observable<any[]> {
    return this.api.get(API_HOST.setup, `${ENDPOINTS.SETUP.get_district_list}/${stateSeq}`);
  }

  // ================= TEHSIL =================
  getTehsils(districtSeq: number): Observable<any[]> {
    return this.api.get(API_HOST.setup, `${ENDPOINTS.SETUP.get_tehsil_list}/${districtSeq}`);
  }

  // ================= CITY (FINAL LEVEL) =================
  getCityTehsilList(): Observable<any[]> {
    return this.api.get(API_HOST.setup, ENDPOINTS.SETUP.get_city_thsl_list);
  }

  // ================= SAVE =================
  saveCountry(payload: any): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_country, payload);
  }

  saveState(payload: any): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_state, payload);
  }

  saveDistrict(payload: any): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_district, payload);
  }

  saveTehsil(payload: any): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_tehsil, payload);
  }

  saveCityTehsil(payload: any): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_city_thsl, payload);
  }
  getAllDocuments(): Observable<any[]> {
    return this.api.get(API_HOST.setup, ENDPOINTS.SETUP.get_all_doc_categories);
  }
  saveDocumentCategory(payload: {
    docCtgrySeq: number;
    ctgryNm: string;
    delFlg: boolean;
  }): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_doc_category, payload);
  }

  getAllDocumentByCategry(categorySeq: number): Observable<any[]> {
    return this.api.get(API_HOST.setup, `${ENDPOINTS.SETUP.GET_ALL_DOCUMENTS}/${categorySeq}`);
  }

  saveDocument(payload: {
    docSeq: number;
    isReq: boolean;
    docCtgryKey: number;
    docNm: string;
    delFlg: boolean;
  }): Observable<any> {
    return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.SAVE_DOCUMENT, payload);
  }

  // ================= FINANCIAL TYPE =================
getAllFinancialTypes(): Observable<any[]> {
  return this.api.get<any[]>(API_HOST.setup, ENDPOINTS.SETUP.get_all_fncl_typ);
}

saveFinancialType(payload: {
  fncTypSeq: number | null;
  typNm: string;
  dscr: string;
  actvFlg: boolean;
  delFlg: number;
}): Observable<any> {
  return this.api.post(API_HOST.setup, ENDPOINTS.SETUP.save_fncl_typ, payload);
}

// ================= FINANCIAL TYPE VALUES =================

getFinancialTypeValues(fncTypSeq: number): Observable<any[]> {
  return this.api.get<any[]>(
    API_HOST.setup,
    `${ENDPOINTS.SETUP.get_fncl_typ_val}${fncTypSeq}`
  );
}


getActvFinanicalType(): Observable<any[]> {
  return this.api.get<any[]>(
    API_HOST.setup,
    ENDPOINTS.SETUP.get_actv_fnc_typ
  );
}

getActvFinanicalVal(fncTypSeqs: number[]): Observable<any> {
  const query = fncTypSeqs
    .map(seq => `fncTypSeqs=${seq}`)
    .join('&');

  return this.api.get<any>(
    API_HOST.setup,
    `${ENDPOINTS.SETUP.get_actv_fnc_val}?${query}`
  );
}

saveFinancialTypeValue(payload: {
  fncValSeq: number | null;
  fncTyp: number;
  actvFlg: boolean;
  dscr: string;
  sortOrdr: number;
  delFlg: number;
}): Observable<any> {
  return this.api.post(
    API_HOST.setup,
    ENDPOINTS.SETUP.save_fncl_typ_val,
    payload
  );
}


// ================= INCOME STATEMENT TYPE =================

getAllIncomeStatementTypes(): Observable<any[]> {
  return this.api.get<any[]>(
    API_HOST.setup,
    ENDPOINTS.SETUP.GET_ALL_INCM_STMNT_TYP
  );
}

getIncomeStatementValues(stmntTypSeq: number): Observable<any[]> {
  return this.api.get<any[]>(
    API_HOST.setup,
    `${ENDPOINTS.SETUP.GET_INCM_STMNT_VAL}${stmntTypSeq}`
  );
}

getActiveIncomeStatementTypes(): Observable<any[]> {
  return this.api.get<any[]>(
    API_HOST.setup,
    ENDPOINTS.SETUP.GET_ACT_INCM_STMNT_TYP
  );
}

getActiveIncomeStatementValues(
  stmntTypSeqs: number[],
): Observable<any> {
  const query = stmntTypSeqs
    .map(
      (sequence) =>
        `stmntTypSeqs=${encodeURIComponent(sequence)}`,
    )
    .join('&');

  return this.api.get<any>(
    API_HOST.setup,
    `${ENDPOINTS.SETUP.GET_ACT_INCM_STMNT_VAL}?${query}`,
  );
}

saveIncomeStatementType(payload: any): Observable<any> {
  return this.api.post(
    API_HOST.setup,
    ENDPOINTS.SETUP.SAVE_INCM_STMNT_TYP,
    payload
  );
}

saveIncomeStatementValue(payload: any): Observable<any> {
  return this.api.post(
    API_HOST.setup,
    ENDPOINTS.SETUP.SAVE_INCM_STMNT_VAL,
    payload
  );
}

// ================= FINANCIAL EXTRACT TYPE =================

getAllFinancialExtractTypes(): Observable<any[]> {
  return this.api.get<any[]>(
    API_HOST.setup,
    ENDPOINTS.SETUP.GET_ALL_FNC_EXTR_TYP
  );
}

getFinancialExtractValues(fncExtrTypSeq: number): Observable<any[]> {
  return this.api.get<any[]>(
    API_HOST.setup,
    `${ENDPOINTS.SETUP.GET_FNC_EXTR_VAL}${fncExtrTypSeq}`
  );
}

getActiveFinancialExtractTypes(): Observable<any[]> {
  return this.api.get<any[]>(
    API_HOST.setup,
    ENDPOINTS.SETUP.GET_ACT_FNC_EXTR_TYP
  );
}

getActiveFinancialExtractValues(): Observable<any[]> {
  return this.api.get<any[]>(
    API_HOST.setup,
    ENDPOINTS.SETUP.GET_ACT_FNC_EXTR_VAL
  );
}

saveFinancialExtractType(payload: any): Observable<any> {
  return this.api.post(
    API_HOST.setup,
    ENDPOINTS.SETUP.SAVE_FNC_EXTR_TYP,
    payload
  );
}

saveFinancialExtractValue(payload: any): Observable<any> {
  return this.api.post(
    API_HOST.setup,
    ENDPOINTS.SETUP.SAVE_FNC_EXTR_VAL,
    payload
  );
}
}
